import i18n from '@/libs/i18n'

/**
 * Quantities cross the wire as an integer, exactly as money does, and an article says
 * how finely it is counted: `decimals` is how many tenths the stored integer is of the
 * unit printed beside it. „Čistilo“ in litres with two decimals sends 25 and reads
 * 0,25 l; a towel has none, sends 4 and reads 4 kos.
 *
 * Mirrors DataStructures/StockDecimals.go. Nothing here does arithmetic on a formatted
 * value, and nothing stores a float — the same rule the money path lives by, for the
 * same reason: 0,7 as a float is 0,6999999999 in somebody's export, and a stocktake is
 * a record of what a hotel owns.
 */

/** The most an article may ask for. Mirrors DataStructures.StockMaxDecimals. */
export const MAX_QUANTITY_DECIMALS = 3

/** 10^decimals: how many stored units make one of the unit on the label. */
export const quantityScale = (decimals?: number | null): number => {
    switch (decimals ?? 0) {
        case 1:
            return 10
        case 2:
            return 100
        case 3:
            return 1000
        default:
            return 1
    }
}

/**
 * A stored quantity as a person reads it: the Slovenian decimal comma through the app's
 * own number formatter, and trailing zeros trimmed unless the article insists on them.
 *
 * Trimming is the point of the rule rather than a nicety. An article counted in litres
 * to two places holds whole litres most of the time, and a register in which every
 * towel reads „4,00“ is a register nobody can skim. What is NOT trimmed is a figure
 * that needs the places to be read at all — 0,25 keeps both, 0,20 keeps one.
 *
 * `unit` is appended where it is given, so that every quantity on a screen carries the
 * article's own word rather than a number somebody has to guess the measure of.
 */
export const formatQty = (stored?: number | null, decimals?: number | null, unit?: string | null): string => {
    const places = clampDecimals(decimals)
    const scale = quantityScale(places)
    const value = (stored ?? 0) / scale

    const trimmed = trailingPlaces(Math.abs(Math.round(stored ?? 0)) % scale, places)

    const text = new Intl.NumberFormat(i18n.global.locale.value, {
        minimumFractionDigits: trimmed,
        maximumFractionDigits: places,
    }).format(value)

    return unit ? `${text} ${unit}` : text
}

/**
 * What a person typed, as the integer the wire carries. `null` is a refusal and the
 * caller shows it as one: empty, not a number, negative places the article does not
 * allow, or more decimals than it allows.
 *
 * Both separators are accepted. A Slovenian keyboard's numeric pad prints a full stop
 * and the language writes a comma, and a field that refused one of them would be a
 * field half the house cannot type into.
 *
 * The parsing is done on the DIGITS and never through parseFloat: 0.29 * 100 is
 * 28.999999999999996 in a float, and that is a hundredth of a litre lost every time
 * somebody receives one.
 */
export const parseQty = (text?: string | null, decimals?: number | null): number | null => {
    const places = clampDecimals(decimals)
    const raw = (text ?? '').trim().replace(',', '.')

    if (raw === '') return null

    const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(raw)
    if (!match) return null

    const [, sign, whole, fraction = ''] = match

    if (whole === '' && fraction === '') return null
    if (fraction.length > places) return null

    const padded = fraction.padEnd(places, '0')
    const digits = `${whole === '' ? '0' : whole}${padded}`

    const value = Number(digits)
    if (!Number.isSafeInteger(value)) return null

    return sign === '-' ? -value : value
}

/**
 * What a quantity is worth: stored units times the price of one DISPLAY unit, brought
 * back down by the scale and rounded half away from zero.
 *
 * It has to agree with DataStructures.QuantityValueCents to the cent — the server
 * recomputes every total it is shown, so a screen rounding differently would show a
 * figure and then be refused for it. The price is per display unit because that is what
 * a supplier prints: a litre of cleaner costs 3,99 € whatever the article's decimals
 * are.
 */
export const quantityValueCents = (
    stored?: number | null,
    decimals?: number | null,
    unitCostCents?: number | null,
): number => {
    const scale = quantityScale(clampDecimals(decimals))
    const product = (stored ?? 0) * (unitCostCents ?? 0)

    if (scale === 1) return product

    const sign = product < 0 ? -1 : 1

    return sign * Math.floor((Math.abs(product) + scale / 2) / scale)
}

/** The step an input of this scale moves by: 1, 0.1, 0.01 or 0.001. */
export const quantityStep = (decimals?: number | null): number => 1 / quantityScale(clampDecimals(decimals))

/** The request bound in stored units — 100000 display units. Mirrors StockMaxStoredQuantity. */
export const maxStoredQuantity = (decimals?: number | null): number => 100000 * quantityScale(clampDecimals(decimals))

const clampDecimals = (decimals?: number | null): number => {
    const places = Math.trunc(decimals ?? 0)

    if (places < 0) return 0
    if (places > MAX_QUANTITY_DECIMALS) return MAX_QUANTITY_DECIMALS

    return places
}

/**
 * How many places a figure actually needs: none where the article counts in whole
 * units and none where the fraction is zero, and otherwise enough that the last
 * non-zero digit is shown.
 */
const trailingPlaces = (fraction: number, places: number): number => {
    if (places === 0 || fraction === 0) return 0

    let needed = places
    let rest = fraction

    while (needed > 0 && rest % 10 === 0) {
        rest /= 10
        needed -= 1
    }

    return needed
}

/**
 * Lifts a line's UNTOUCHED default quantity to one whole unit of the article just
 * picked.
 *
 * Every line dialog in this module starts a new row at 1, which is one piece while
 * nothing is chosen and becomes a HUNDREDTH of a litre the moment somebody picks an
 * article counted to two places — a delivery note for 0,01 l of cleaner, typed by
 * nobody. Five dialogs had the same fault and `PropertyStockDocuments.onLineItem` was
 * the only one repaired, so the rule is a function rather than a paragraph copied five
 * times: a default is lifted, and anything a person actually typed is left alone.
 *
 * Exactly 1 is what counts as untouched, deliberately. It is the only figure this
 * module ever puts in the field by itself, and a storekeeper who means one stored unit
 * of a hundredths article types „0,01“ — which arrives here as 1 and is lifted to 100.
 * That is a hundredth nobody can enter by picking an article FIRST; entering it after
 * the article is chosen, which is the order every one of these dialogs is filled in,
 * works. The alternative — a flag per row remembering whether a person touched the
 * field — is state on every line of every document to save that one keystroke.
 */
export const liftDefaultQuantity = (line: { quantity: number }, decimals?: number | null): void => {
    if (line.quantity === 1) line.quantity = quantityScale(decimals)
}
